/**
 * GA4 / GTM dataLayer mirror.
 *
 * Fires alongside every user-facing ft.track call so the salon's GTM
 * container sees the same events with matching event_id / transaction_id.
 *
 * Architecture (per Phase 1c plan):
 *   - FireTrack is canonical for paid-ad conversions (Meta CAPI + Google
 *     Ads OCA via the server-side dispatcher). The browser fires + the
 *     webhook fires, both with event_id = squareBookingId, FireTrack
 *     dedupes server-side.
 *   - dataLayer is GA4-only — engagement signals + a backup analytics
 *     record of bookings. No Google Ads tags fire from GTM; the salon's
 *     ad spend ROI is computed off the FireTrack→Google-Ads-OCA path.
 *
 * The two pipes are independent. Both can fire for the same booking;
 * only the FireTrack side carries dedup discipline (event_id pin).
 */

interface DataLayerWindow extends Window {
  dataLayer?: Array<Record<string, unknown>>;
}

/**
 * Push an event to window.dataLayer. Lazy-init the array so we play
 * nicely whether GTM has loaded yet or not — pushed events are picked
 * up by GTM whenever its bootstrap completes.
 */
export function pushDataLayer(
  event: string,
  payload?: Record<string, unknown>,
): void {
  if (typeof window === 'undefined') return;
  const w = window as DataLayerWindow;
  if (!w.dataLayer) w.dataLayer = [];
  w.dataLayer.push({ event, ...(payload ?? {}) });
}
